import { COMPANY } from '@/shared/company';
import { apiFetch } from '@/auth/api';
import type { ModuleSlug } from '@/styles/tokens';

/**
 * ⌘K data layer — the pure half of the launcher, kept out of the component
 * so the ranking and grouping rules are testable on their own.
 *
 * Two feeds, one scale:
 *   · PAGES come from the nav registry, client-side, already filtered by the
 *     user's effective pages (see `navVisibility.ts`) — no round-trip.
 *   · RECORDS come from `GET /api/search` (OmniSearchController), which fans
 *     out to bookings, guests, files, suppliers, agents, issues and welcome
 *     meetings, each behind the page grant that shows it.
 * Page scores are set on the server's scale (OmniSearchScoring) so the
 * groups can be ordered by their best hit: an exact booking ref (1000)
 * outranks an exact page title (600), which outranks a name prefix (450).
 */

// ── Server contract (mirrors OmniSearch.cs, camelCase) ──────────────

export interface ServerHit {
  kind: string;
  id: string;
  title: string;
  subtitle?: string | null;
  to: string;
  href?: string | null;
  score: number;
  ref?: string | null;
}

export interface ServerGroup {
  key: string;
  label: string;
  items: ServerHit[];
  best: number;
}

export interface ServerResult {
  q: string;
  groups: ServerGroup[];
  searched: string[];
  failed: string[];
  elapsedMs: number;
}

/** Below this the server is not asked (it would answer empty anyway). */
export const SERVER_MIN_LENGTH = 2;
/** Results per server group. */
export const SERVER_PER_GROUP = 6;

export async function fetchOmniSearch(q: string, signal: AbortSignal): Promise<ServerResult> {
  const res = await apiFetch<ServerResult>(
    `/search?q=${encodeURIComponent(q)}&limit=${SERVER_PER_GROUP}`,
    { method: 'GET', signal },
  );
  return {
    q: res?.q ?? q,
    groups: Array.isArray(res?.groups) ? res.groups : [],
    searched: Array.isArray(res?.searched) ? res.searched : [],
    failed: Array.isArray(res?.failed) ? res.failed : [],
    elapsedMs: res?.elapsedMs ?? 0,
  };
}

// ── The flat list the overlay renders ───────────────────────────────

export interface ResultItem {
  /** Unique across the whole list — React key AND the option id seed. */
  key: string;
  kind: string;
  /** The short uppercase tag in the left column (module name / record type). */
  tag: string;
  title: string;
  subtitle?: string;
  /** In-app route. */
  to: string;
  /** A file to open instead (attachments). */
  href?: string;
  /** CSS colour (always a `var(--…)`, never a literal — see CLAUDE.md). */
  accent: string;
  score: number;
}

export interface ResultGroup {
  key: string;
  label: string;
  items: ResultItem[];
  best: number;
}

/** Record-type tag shown in the left column of a server row. */
export const KIND_TAG: Record<string, string> = {
  booking: 'Booking',
  guest: 'Guest',
  // A getter: the key agent is the runtime company profile's (Admin ▸ Company).
  get 'kuoni-contact'() {
    return COMPANY.keyAgent;
  },
  attachment: 'File',
  supplier: 'Supplier',
  agent: 'Agent',
  issue: 'Issue',
  meeting: 'Meeting',
};

/**
 * Per-group thread colour. Bookings and files take the Logs group's azure
 * (`--c-res`, one of the two genuinely cold accents, so a booking row reads
 * apart from the warm page rows); every other record is a CS/OPS Portal
 * record and takes that module's accent.
 */
const BOOKINGS_ACCENT = 'var(--c-res)';
const GROUP_ACCENT: Record<string, string> = {
  bookings: BOOKINGS_ACCENT,
  attachments: BOOKINGS_ACCENT,
};
const DEFAULT_RECORD_ACCENT = 'var(--c-coportal)';

/** Fixed tie-break order when two groups have the same best score. */
export const GROUP_ORDER = [
  'bookings', 'pages', 'guests', 'attachments', 'suppliers', 'agents', 'issues', 'meetings',
] as const;

// ── Pages, on the server's scale ────────────────────────────────────

export interface IndexedPage {
  to: string;
  label: string;
  module: ModuleSlug;
  moduleLabel: string;
  haystack: string;
  accent: string;
}

export const PAGE_SCORE = {
  exact: 600,
  prefix: 450,
  contains: 250,
  haystack: 150,
  /** Pages of the module you are standing in float up. */
  activeModuleBonus: 40,
} as const;

export function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function rankPages(
  pages: readonly IndexedPage[],
  q: string,
  limit = 8,
  activeModule?: ModuleSlug | null,
): ResultItem[] {
  const needle = fold(q);
  if (!needle) return [];
  const scored: Array<{ page: IndexedPage; score: number }> = [];
  for (const p of pages) {
    const label = fold(p.label);
    let score: number;
    if (label === needle) score = PAGE_SCORE.exact;
    else if (label.startsWith(needle)) score = PAGE_SCORE.prefix;
    else if (label.includes(needle)) score = PAGE_SCORE.contains;
    else if (p.haystack.includes(needle)) score = PAGE_SCORE.haystack;
    else continue;
    if (activeModule && p.module === activeModule) score += PAGE_SCORE.activeModuleBonus;
    scored.push({ page: p, score });
  }
  scored.sort((a, b) => b.score - a.score || a.page.label.localeCompare(b.page.label));
  return scored.slice(0, limit).map(({ page, score }) => ({
    key: `page:${page.to}`,
    kind: 'page',
    tag: page.moduleLabel,
    title: page.label,
    to: page.to,
    accent: page.accent,
    score,
  }));
}

// ── Booking-ref shortcut ────────────────────────────────────────────

/** Booking refs are numeric, 6–7 digits (tolerate 5–9, a leading `#`, spaces). */
export function asBookingRef(q: string): string | null {
  const compact = q.replace(/\s+/g, '').replace(/^#/, '');
  return /^\d{5,9}$/.test(compact) ? compact : null;
}

/**
 * The instant "open this ref in Logs" row: a typed ref is actionable before
 * the server answers (Enter works immediately). It ranks just under an exact
 * server hit and is dropped once the server returns that very booking.
 */
export function refShortcut(ref: string): ResultItem {
  return {
    key: `ref:${ref}`,
    kind: 'booking',
    tag: 'Logs',
    title: `Open booking ${ref}`,
    subtitle: 'all service lines for this ref',
    to: `/coportal/logs-all?refs_or=${encodeURIComponent(ref)}`,
    accent: BOOKINGS_ACCENT,
    score: 900,
  };
}

// ── Merge ───────────────────────────────────────────────────────────

export interface MergeInput {
  pages: ResultItem[];
  server: ServerGroup[];
  /** Pass the shortcut row when the query is a ref and the user can open Logs. */
  shortcut?: ResultItem | null;
  /** Route rewrite for rows whose module the user reaches another way. */
  resolveTo?: (hit: ServerHit) => string;
}

/**
 * Pages + server groups → the ordered group list the overlay renders.
 * Groups are ordered by their best score, ties broken by `GROUP_ORDER`;
 * items keep the order the scorer gave them.
 */
export function mergeResults({ pages, server, shortcut, resolveTo }: MergeInput): ResultGroup[] {
  const groups: ResultGroup[] = [];

  for (const g of server) {
    const items: ResultItem[] = g.items.map((h) => ({
      key: `${h.kind}:${h.id}`,
      kind: h.kind,
      tag: KIND_TAG[h.kind] ?? h.kind,
      title: h.title,
      subtitle: h.subtitle ?? undefined,
      to: resolveTo ? resolveTo(h) : h.to,
      href: h.href ?? undefined,
      accent: GROUP_ACCENT[g.key] ?? DEFAULT_RECORD_ACCENT,
      score: h.score,
    }));
    if (items.length) groups.push({ key: g.key, label: g.label, items, best: items[0]?.score ?? 0 });
  }

  if (shortcut) {
    const bookings = groups.find((g) => g.key === 'bookings');
    const ref = shortcut.key.slice('ref:'.length);
    const exact = server
      .find((g) => g.key === 'bookings')
      ?.items.some((h) => (h.ref ?? '').toLowerCase() === ref.toLowerCase());
    if (!exact) {
      if (bookings) {
        bookings.items.unshift(shortcut);
        bookings.best = Math.max(bookings.best, shortcut.score);
      } else {
        groups.push({ key: 'bookings', label: 'Bookings', items: [shortcut], best: shortcut.score });
      }
    }
  }

  if (pages.length) groups.push({ key: 'pages', label: 'Pages', items: pages, best: pages[0]?.score ?? 0 });

  const order = (k: string) => {
    const i = (GROUP_ORDER as readonly string[]).indexOf(k);
    return i < 0 ? GROUP_ORDER.length : i;
  };
  return groups.sort((a, b) => b.best - a.best || order(a.key) - order(b.key));
}

/** The groups flattened, in render order — what the arrow keys walk. */
export function flatten(groups: readonly ResultGroup[]): ResultItem[] {
  return groups.flatMap((g) => g.items);
}
